import { c as _c } from "react/compiler-runtime";
import type { VaultKind } from '@onefootprint/request-types';
import type { GraphOperationResult } from '@onefootprint/request-types/dashboard';
import { cx } from 'class-variance-authority';
import { motion } from 'framer-motion';
import { Suspense, useState } from 'react';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import useSidebar from '@/components/playbook-builder/hooks/use-sidebar';
import useNodeContext from '../../../hooks/use-node-context';
import useStore from '../../../hooks/use-store';
import AddButton from '../../add-button';
import AddNodeDialog from '../../add-node-dialog';
import type { ApplyEditMutation } from '../base-node';
type AddAfterNodeProps = {
  nodeId: string;
  mutation: ApplyEditMutation;
  subject: VaultKind;
};
const AddAfterNode = t0 => {
  const $ = _c(22);
  const {
    nodeId,
    mutation,
    subject
  } = t0;
  const {
    addAfterNodeOperations,
    successors
  } = useNodeContext(nodeId);
  const hasChild = successors.length > 0;
  const {
    isAnyComponentDirty
  } = useDirty();
  const {
    data: t1
  } = useStore();
  const {
    graph
  } = t1;
  const {
    onOperationComplete
  } = useSidebar();
  const [open, setOpen] = useState(false);
  let t2;
  if ($[0] !== isAnyComponentDirty || $[1] !== mutation.isPending) {
    t2 = () => {
      if (mutation.isPending || isAnyComponentDirty) {
        return;
      }
      setOpen(true);
    };
    $[0] = isAnyComponentDirty;
    $[1] = mutation.isPending;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleClick = t2;
  let t3;
  if ($[3] !== onOperationComplete) {
    t3 = operation => {
      setOpen(false);
      onOperationComplete(operation);
    };
    $[3] = onOperationComplete;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleClose = t3;
  const currentNode = graph.nodes.find(node => node.id === nodeId);
  const shouldHideAddAfterNode = () => {
    if (!currentNode) {
      return false;
    }
    if (currentNode.kind === "branch") {
      return true;
    }
    const hasPlaceholderSuccessor = successorId => graph.nodes.find(node_0 => node_0.id === successorId)?.kind === "placeholder";
    if (currentNode.kind === "conditional") {
      const allBranches = [...currentNode.conditionalBranches.map(_temp), currentNode.defaultBranch];
      return allBranches.some(branch => hasPlaceholderSuccessor(branch.successor));
    }
    return hasPlaceholderSuccessor(currentNode.successor);
  };
  if (!addAfterNodeOperations.length || shouldHideAddAfterNode()) {
    return null;
  }
  const bottomOffset = hasChild ? "-bottom-11" : "-bottom-9";
  let t4;
  if ($[5] !== bottomOffset) {
    t4 = cx("flex absolute right-0 left-0 justify-center pointer-events-auto", bottomOffset);
    $[5] = bottomOffset;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      opacity: 0
    };
    t6 = {
      opacity: 0
    };
    t7 = {
      opacity: 1
    };
    t8 = {
      duration: 0.2
    };
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] !== handleClick) {
    t9 = <AddButton onClick={handleClick} />;
    $[11] = handleClick;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== addAfterNodeOperations || $[14] !== handleClose || $[15] !== open || $[16] !== subject) {
    t10 = <Suspense fallback={null}><AddNodeDialog open={open} onClose={handleClose} operations={addAfterNodeOperations} subject={subject} /></Suspense>;
    $[13] = addAfterNodeOperations;
    $[14] = handleClose;
    $[15] = open;
    $[16] = subject;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t4 || $[20] !== t9) {
    t11 = <motion.div className={t4} initial={t5} animate={t6} whileHover={t7} transition={t8}>{t9}{t10}</motion.div>;
    $[18] = t10;
    $[19] = t4;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
};
export default AddAfterNode;
function _temp(b) {
  return b.branch;
}